import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { genericRequestError } from './api_errors';

const csrfTokenTag = document.querySelector('meta[name="csrf-token"]');

if (csrfTokenTag) {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfTokenTag.getAttribute('content') || '';
}

export function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

interface BodyOptions {
  // Top-level body keys whose values are sent as-is, e.g. a hash keyed by user-entered names.
  verbatimKeys?: string[];
  [key: string]: unknown;
}

function snakecaseBody(body, verbatimKeys: string[] = []) {
  if (!body) return null;
  // FormData is not a plain object and isn't enumerable via Object.keys, so snakecase-keys
  // walks an empty key list and returns `{}` - silently discarding multipart payloads.
  // Pass it through unchanged; axios handles the multipart serialization on its own.
  if (body instanceof FormData) return body;

  const exclude = [/\[|\]|]|_destroy/];
  const rest = { ...body };
  const verbatim = {};
  verbatimKeys.forEach((key) => {
    if (!(key in rest)) return;
    verbatim[key] = rest[key];
    delete rest[key];
  });

  // deep: false snake-cases the top-level key so Rails still finds it, and leaves the value untouched
  return { ...snakecaseKeys(rest, { exclude }), ...snakecaseKeys(verbatim, { exclude, deep: false }) };
}

function snakecaseParams(options) {
  if (options.params) {
    options = { ...options, params: snakecaseBody(options.params) };
  }

  return options;
}

// Rejects with Rails' `errors` when the server sends them, unchanged. Anything else (a 5xx, a network failure, a 4xx
// without `errors`) rejects with genericRequestError, so callers can always read a list of messages; read it with
// helpers/api_errors. Cancellations are rethrown as they are.
function rejection(error) {
  if (axios.isCancel(error)) return error;

  const errors = error?.response?.data?.errors;
  if (errors) {
    if (typeof errors === 'object') Object.defineProperty(errors, 'cause', { configurable: true, value: error });
    return errors;
  }

  return genericRequestError(error);
}

function makeRequest(method, ...args) {
  const instance = defaultAxiosInstance();

  return instance[method](...args)
    .then((response) => {
      try {
        return camelcaseKeys(response.data, { deep: true });
      } catch (error) {
        return response.data;
      }
    })
    .catch((error) => {
      throw rejection(error);
    });
}

function makeBodyRequest(method, path, body, { verbatimKeys, ...options }: BodyOptions) {
  return makeRequest(method, path, snakecaseBody(body, verbatimKeys), options);
}

export function put(path, body, options = {}): Promise<any> {
  return makeBodyRequest('put', path, body, options);
}

export function post(path, body?, options = {}): Promise<any> {
  return makeBodyRequest('post', path, body, options);
}

export function patch(path, body, options = {}): Promise<any> {
  return makeBodyRequest('patch', path, body, options);
}

export function get(path, options = {}): Promise<any> {
  return makeRequest('get', path, snakecaseParams(options));
}

// Like `get` but returns the response body verbatim — no camelCase conversion. Use when the
// payload has keys that must survive as-is (e.g. hyphenated CSS style keys).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getRaw(path, options = {}): Promise<any> {
  return defaultAxiosInstance().get(path, options).then(response => response.data);
}

export function deleteRequest(path, options = {}): Promise<any> {
  return makeRequest('delete', path, snakecaseParams(options));
}

export function defaultAxiosInstance() {
  return axios.create({
    // This is required for `request.xhr?` to return true in Rails-land
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
}
